import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { generateChapterLink } from '@/utils/utils';
import { getObraImageUrl, PLACEHOLDER_OBRA_COVER } from '@/utils/image-utils';
import type { ReadingTabEntry } from '@/utils/reading-tabs';
import { removeReadingTab } from '@/utils/reading-tabs';

type Props = {
    tabs: ReadingTabEntry[];
    activeCapId: number | null;
};

/** Faixa de abas estilo Chrome (só desktop). Cada aba abre o capítulo guardado. */
export default function ChapterReadingTabBar({ tabs, activeCapId }: Props) {
    if (tabs.length === 0) return null;

    return (
        <div
            className="hidden md:flex fixed top-0 left-0 right-0 z-[60] h-9 min-h-9 items-stretch border-b border-white/[0.08] bg-[#1a1a1c] pl-1 pr-1 pt-0.5"
            role="tablist"
            aria-label="Capítulos recentes"
        >
            <div className="flex min-w-0 flex-1 items-end gap-0.5 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-white/15">
                {tabs.map((tab) => {
                    const href = generateChapterLink(tab.capId);
                    const active = activeCapId != null && tab.capId === activeCapId;
                    const thumbUrl = getObraImageUrl(tab.obraId, tab.obraImagem ?? null);
                    const thumb = thumbUrl ?? PLACEHOLDER_OBRA_COVER;
                    return (
                        <div
                            key={tab.obraId}
                            className={`group relative flex max-w-[11rem] min-w-[7.5rem] shrink-0 items-center rounded-t-md border border-b-0 px-1.5 py-0.5 transition-colors ${
                                active
                                    ? 'border-white/12 bg-zinc-900/95 text-white'
                                    : 'border-transparent bg-white/[0.04] text-white/75 hover:bg-white/[0.07] hover:text-white'
                            }`}
                        >
                            <Link
                                to={href}
                                className="flex min-w-0 flex-1 items-center gap-1.5 py-0.5"
                                title={`${tab.obraNome} — ${tab.capLabel}`}
                            >
                                <span className="relative h-5 w-5 shrink-0 overflow-hidden rounded-sm border border-white/10 bg-black/40">
                                    <img
                                        src={thumb}
                                        alt=""
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.src = PLACEHOLDER_OBRA_COVER;
                                        }}
                                    />
                                </span>
                                <span className="min-w-0 flex-1 truncate text-left text-[11px] font-medium leading-tight">
                                    {tab.capLabel}
                                </span>
                            </Link>
                            <button
                                type="button"
                                className="ml-0.5 shrink-0 rounded p-0.5 text-white/0 transition-colors group-hover:text-white/45 hover:!bg-white/10 hover:!text-white/90"
                                aria-label="Fechar aba"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeReadingTab(tab.obraId);
                                }}
                            >
                                <X size={12} strokeWidth={2.5} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
